/**
 * Directory sorting and title formatting.
 *
 * Sorting used to be name-only (getNameSortKey against 'lastName'/'firstName'),
 * which is why the A–Z rail and the letter group headers could always be shown.
 * The sortable column header adds department, office and tenure, and a letter
 * index is meaningless under those — so callers must check isNameSort() before
 * grouping rows by first letter.
 */
import { getNameSortKey } from './nameUtils';

export const SORT_COLUMNS = [
  { id: 'lastName', label: 'Name', letterIndexed: true },
  { id: 'department', label: 'Department', letterIndexed: false },
  { id: 'officeLocation', label: 'Office', letterIndexed: false },
  { id: 'tenure', label: 'Years of Service', letterIndexed: false },
];

const NAME_SORTS = ['lastName', 'firstName'];

/** Letter grouping and the A–Z rail only make sense for name sorts. */
export function isNameSort(sortBy) {
  return NAME_SORTS.includes(sortBy);
}

// Blank values sort last in every column rather than clustering at the top,
// where they would look like real data.
function blankLast(a, b) {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return null;
}

function byName(a, b, sortBy) {
  const keyA = getNameSortKey(a.displayName, isNameSort(sortBy) ? sortBy : 'lastName');
  const keyB = getNameSortKey(b.displayName, isNameSort(sortBy) ? sortBy : 'lastName');
  if (keyA !== keyB) return keyA.localeCompare(keyB);
  return (a.displayName || '').localeCompare(b.displayName || '');
}

/**
 * Total ordering for any supported column. Always falls back to name so the
 * order is stable — two people in the same department never swap places
 * between renders.
 */
export function compareEmployees(a, b, sortBy = 'lastName') {
  if (isNameSort(sortBy)) return byName(a, b, sortBy);

  if (sortBy === 'tenure') {
    // Longest tenure first, so the earliest hire date wins.
    const blanks = blankLast(a.hireDate, b.hireDate);
    if (blanks !== null) return blanks || byName(a, b, sortBy);
    if (a.hireDate !== b.hireDate) return a.hireDate < b.hireDate ? -1 : 1;
    return byName(a, b, sortBy);
  }

  const valA = a[sortBy];
  const valB = b[sortBy];
  const blanks = blankLast(valA, valB);
  if (blanks !== null) return blanks || byName(a, b, sortBy);
  const cmp = String(valA).localeCompare(String(valB));
  return cmp !== 0 ? cmp : byName(a, b, sortBy);
}

export function sortEmployees(employees, sortBy = 'lastName') {
  return employees.slice().sort((a, b) => compareEmployees(a, b, sortBy));
}

// Rank words that carry no information once abbreviated. Ordered longest-first
// so "Vice President" is consumed before "President" could be.
const TITLE_ABBREVIATIONS = [
  [/\bVice President\b/g, 'VP'],
  [/\bExecutive Vice President\b/g, 'EVP'],
  [/\bSenior\b/g, 'Sr.'],
  [/\bJunior\b/g, 'Jr.'],
  [/\bAssistant\b/g, 'Asst.'],
  [/\bAssociate\b/g, 'Assoc.'],
  [/\bAdministrative\b/g, 'Admin.'],
];

/**
 * Shorten a job title so it survives a fixed column without truncating
 * mid-word. The untruncated original still goes in a `title` attribute — this
 * only buys horizontal room, it never replaces the full value.
 */
export function abbreviateTitle(title) {
  if (!title) return '';
  return TITLE_ABBREVIATIONS.reduce(
    (out, [pattern, replacement]) => out.replace(pattern, replacement),
    title,
  ).trim();
}
